import React, { FC, useState, type JSX } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import AppTableRow from '../TableRow/TableRow';
import Skeleton from '@mui/lab/Skeleton';
import {TableContainerRoot, TableHeadUpperCase, TableMinWidth, TablePaper, TableRoot} from './styles';

/**
 * @deprecated
 */
export interface IActionItem {
  icon: JSX.Element;
  label: string;
  onClick: (data: any) => void;
  id?: string | number
}

interface HeadCell {
  id: string;
  label: string;
  sortBy?: string
}

export type SortDir = 'asc' | 'desc';

/**
 * @deprecated
 */
export interface ITable {
  /** an array of id/labels that correlate to the data*/
  headCells: Array<HeadCell>;
  /** an array of data entries*/
  rows: Array<any>;
  /** (optional) if true, a hot dog menu will render for the row*/
  hasRowAction?: boolean;
  /** (optional) if true, a loading animation plays*/
  isLoading?: boolean;
  /** (optional) if true, row titles are formatted to uppercase*/
  uppercaseTitle?: boolean;
  /** (optional) an array of actions for the card. each takes an icon, a label, and an onClick handler. Renders if actionType = 'list'*/
  actionItems?: Array<IActionItem>;
  /** (optional) action items that are rendered based on the data id*/
  conditionalActionItems?: Array<IActionItem>;
  /** (optional) affects how actions are displayed to user*/
  actionType: "button" | "list",
  /** (optional) a function component to render an action button if actionType = 'button'*/
  actionButtons?: (props: { id: any, item: any }) => JSX.Element,
  /** (optional) a function component to render at the bottom of the row component*/
  rowMessageComponents?: Array<{ id: any, component: JSX.Element }>;
  /** (optional) conditionally renders arrows to sort columns*/
  isSortable?: boolean;
  /** (optional) a function component to render within the table row*/
  RowSubComponent?: (props: { id: any, item: any }) => JSX.Element;
  /** (optional) if true, renders a box shadow to the component*/
  isShadow?: boolean;
  /** (optional) the handler function for a row sort action*/
  handleSortRequest?: (property: string, order:SortDir) => void;
  /** (optional) the default sortBy value*/
  sortBy?: string;
  /** (optional) the default sort direction*/
  sortDir?: SortDir;
  /** (optional) Object mapping the columnId as the key to the className applied to the row cell*/
  rowClasses?: { [columnId: string]: string;}
}

const defaultActionItems = [
  {
    icon: <PrintIcon fontSize="medium" color="primary" />,
    label: 'Print',
    onClick: () => {
      console.log('print');
    },
  },
];


const LoadingSkeleton = () => {
  return (
    <TableContainer sx={{ width: '100%' }}>
      <Table sx={{ width: '100%' }}>
        <TableHead>
          <TableRow>
            {[...Array(10).keys()].map((_, index) => (
              <TableCell key={index} align="left" padding="normal">
                <Skeleton width={107} height={40} />
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {[...Array(10).keys()].map((_, index) => (
            <TableRow key={index}>
              {[...Array(10).keys()].map((_, index) => (
                <TableCell key={index} align="left" padding="normal">
                  <Skeleton width={107} height={40} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

/**
 * Old Table component
 * @param headCells
 * @param rows
 * @param hasRowAction
 * @param RowSubComponent
 * @param actionType
 * @param uppercaseTitle
 * @param isLoading
 * @param isSortable
 * @param actionItems
 * @param actionButtons
 * @param conditionalActionItems
 * @param rowMessageComponents
 * @param isShadow
 * @param handleSortRequest
 * @param sortBy
 * @param sortDir
 * @param rowClasses
 * @constructor
 * @deprecated
 */
const AppTable: FC<ITable> = ({
  headCells,
  rows,
  hasRowAction = false,
  RowSubComponent,
  actionType = "button",
  uppercaseTitle = true,
  isLoading = false,
  isSortable = false,
  actionItems = defaultActionItems,
  actionButtons,
  conditionalActionItems,
  rowMessageComponents,
  isShadow = false,
  handleSortRequest,
  sortBy,
  sortDir,
  rowClasses,
}: ITable) => {
  const [order, setOrder] = useState<SortDir>(sortDir ? sortDir : 'asc');
  const [orderBy, setOrderBy] = useState<string>(sortBy ? sortBy : '');

  const handleRequestSort = (cell: HeadCell) => {
    const property = cell.id;
    const isAsc = (orderBy === cell.sortBy || orderBy === property) && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    handleSortRequest && handleSortRequest(property, isAsc ? 'desc' : 'asc');
  };

  const getArrowDirection = (headCell: HeadCell) =>
    orderBy === headCell.id || (headCell.sortBy && orderBy === headCell.sortBy) ? order : 'asc';

  return (
    <TableRoot>
      <TablePaper>
        {
          isLoading ? <LoadingSkeleton /> : <TableContainerRoot isShadow={isShadow}>
            <TableMinWidth
              size="medium"
              sx={{'& thead th': {fontWeight: 500}}}
            >
              <TableHead sx={{...(isShadow && {backgroundColor: '#fafafa !important'})}}>
                <TableRow>
                  {
                    headCells?.map((headCell, inx) => (
                      <TableHeadUpperCase
                        key={inx}
                        sortDirection={orderBy === headCell.id ? order : false}
                        isUppercase={uppercaseTitle}
                      >
                        {isSortable ?
                          <TableSortLabel
                            active={isSortable && (orderBy === headCell.id || orderBy === headCell.sortBy)}
                            direction={getArrowDirection(headCell)}
                            onClick={() => {
                              handleRequestSort(headCell)}}>
                              {headCell.label}
                          </TableSortLabel>
                        :
                          <Typography>
                            {headCell.label}
                          </Typography>
                        }
                      </TableHeadUpperCase>
                    ))
                  }
                  {actionType === "list" && hasRowAction && actionItems?.length > 0 ? <TableCell /> : actionType === "button" && hasRowAction && <TableHeadUpperCase isUppercase={uppercaseTitle} sx={{
                    fontSize: '.85em',
                    fontWeight: '400',
                    color: 'rgba(0, 0, 0, 0.87)',
                    fontFamily: "Inter",
                    textTransform: 'capitalize',
                    whiteSpace: 'nowrap',
                  }}>
                    ACTIONS
                  </TableHeadUpperCase>}

                </TableRow>
              </TableHead>
              <TableBody>
                {
                  rows.map((row: any, index: number) => (
                    <AppTableRow
                      row={row}
                      columnOrder={headCells.map(headCell => headCell.id)}
                      key={index}
                      inx={index}
                      actionItems={actionItems}
                      hasRowAction={hasRowAction}
                      hasRowSubComponent={!!RowSubComponent}
                      RowSubComponent={RowSubComponent}
                      actionType={actionType}
                      actionButtons={actionButtons}
                      rowMessageComponents={rowMessageComponents}
                      conditionalActionItems={conditionalActionItems}
                      className={rowClasses || undefined}
                    />
                  ))
                }
              </TableBody>
            </TableMinWidth>
          </TableContainerRoot>
        }
      </TablePaper>
    </TableRoot>
  );
};

export default AppTable;
